import * as React from "react";

import { getApiErrorCode, getApiErrorMessage } from "api/apiError";
import { Alert, Button, FormField, Input, notify, OtpInput, PasswordInput } from "Components/ui";
import { WhatsApp } from "Components/ui/icons";
import { formatCountdown, useCountdown } from "hooks/useCountdown";
import type { ProfileData } from "../data";
import {
  displayPhone,
  formatPhoneInput,
  isValidIndianPhone,
  PHONE_INVALID_MESSAGE,
} from "../phone";

const CODE_LENGTH = 6;

/** Where the flow starts: on the new-number form, or on code entry for a code already sent. */
export type PhoneFlowStart =
  | { step: "number" }
  | {
      step: "code";
      /** Full number the code went to; resends go there. */
      target: string;
      change: boolean;
      /** Seconds until "Resend code" is allowed. */
      resendIn: number;
    };

interface PhoneFlowProps {
  start: PhoneFlowStart;
  onClose: () => void;
  profile: ProfileData;
}

/**
 * Changes or verifies the WhatsApp number, in place in the profile drawer. A new number needs the
 * current password and only replaces the current one once the code sent to it is confirmed;
 * until then sign-in codes keep going to the current number.
 */
const PhoneFlow: React.FC<PhoneFlowProps> = ({ start, onClose, profile }) => {
  const { user, requestPhoneCode, confirmPhone } = profile;
  const [step, setStep] = React.useState<"number" | "code">("number");
  const [phone, setPhone] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [target, setTarget] = React.useState("");
  const [change, setChange] = React.useState(true);
  const [code, setCode] = React.useState("");
  const [phoneError, setPhoneError] = React.useState<string | null>(null);
  const [passwordError, setPasswordError] = React.useState<string | null>(null);
  const [codeError, setCodeError] = React.useState<string | null>(null);
  const [formError, setFormError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState<null | "send" | "resend" | "confirm">(null);
  const [resendIn, restartResend] = useCountdown(0);
  const codeInput = React.useRef<HTMLInputElement | null>(null);

  // Each start begins clean, at the step it was opened for.
  React.useEffect(() => {
    setStep(start.step);
    setPhone("");
    setPassword("");
    setCode("");
    setPhoneError(null);
    setPasswordError(null);
    setCodeError(null);
    setFormError(null);
    if (start.step === "code") {
      setTarget(start.target);
      setChange(start.change);
      restartResend(start.resendIn);
    } else {
      setChange(true);
    }
  }, [start, restartResend]);

  const describeSendError = (err: unknown) => {
    const errorCode = getApiErrorCode(err);
    if (errorCode === "PROFILE_PHONE_INVALID") {
      setPhoneError(getApiErrorMessage(err, PHONE_INVALID_MESSAGE));
    } else if (
      errorCode === "AUTH_PASSWORD_MISMATCH" ||
      errorCode === "PROFILE_PASSWORD_REQUIRED"
    ) {
      setPasswordError(getApiErrorMessage(err));
    } else {
      setFormError(getApiErrorMessage(err, "The code couldn't be sent. Try again shortly."));
    }
  };

  const sendToNewNumber = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPhoneError(null);
    setPasswordError(null);
    setFormError(null);
    const normalized = formatPhoneInput(phone);
    if (!isValidIndianPhone(normalized)) {
      setPhoneError(PHONE_INVALID_MESSAGE);
      return;
    }
    if (normalized === user?.phoneNumber) {
      setPhoneError("That's already your number.");
      return;
    }
    if (!password) {
      setPasswordError("Enter your current password");
      return;
    }
    setBusy("send");
    try {
      const res = await requestPhoneCode({ phoneNumber: normalized, currPassword: password });
      setTarget(normalized);
      setChange(res.change);
      setCode("");
      setCodeError(null);
      restartResend(res.resendAfterSeconds);
      setStep("code");
    } catch (err) {
      describeSendError(err);
    } finally {
      setBusy(null);
    }
  };

  const resend = async () => {
    setCodeError(null);
    setFormError(null);
    setBusy("resend");
    try {
      // The server lets a resend to the same pending number skip the password; it's sent anyway
      // when we have it, in case the earlier code ran out.
      const res = await requestPhoneCode(
        change ? { phoneNumber: target, currPassword: password || undefined } : {}
      );
      setCode("");
      restartResend(res.resendAfterSeconds);
      notify.success("New code sent on WhatsApp");
    } catch (err) {
      const errorCode = getApiErrorCode(err);
      if (errorCode === "PROFILE_PASSWORD_REQUIRED" || errorCode === "AUTH_PASSWORD_MISMATCH") {
        // The code ran out and we don't have the password (the dialog was reopened): start over.
        setStep("number");
        setPhone(target);
        setPasswordError("Enter your current password to send a new code");
      } else {
        setFormError(getApiErrorMessage(err, "The code couldn't be sent. Try again shortly."));
      }
    } finally {
      setBusy(null);
    }
  };

  const confirm = async (value: string = code) => {
    if (value.length < CODE_LENGTH || busy) {
      return;
    }
    setCodeError(null);
    setFormError(null);
    setBusy("confirm");
    try {
      const changed = await confirmPhone(value);
      notify.success(changed ? "WhatsApp number changed" : "Number verified", {
        description: changed
          ? `Sign-in codes now go to ${displayPhone(target)}.`
          : "Sign-in codes keep going to this number.",
      });
      onClose();
    } catch (err) {
      setCodeError(getApiErrorMessage(err, "That code didn't work."));
      setCode("");
      if (getApiErrorCode(err) === "AUTH_OTP_EXHAUSTED") {
        // A fresh code is needed and can be requested straight away.
        restartResend(0);
      }
      // Ready for the next try without another click.
      codeInput.current?.focus();
    } finally {
      setBusy(null);
    }
  };

  const numberTitle = user?.phoneNumber ? "Change WhatsApp number" : "Add a WhatsApp number";

  return (
    <section
      aria-label={step === "number" ? numberTitle : "Enter the WhatsApp code"}
      className="rounded-card border border-border bg-surface-muted/40 p-4"
    >
      {step === "number" ? (
        <form noValidate onSubmit={sendToNewNumber} className="space-y-5">
          <div className="space-y-1">
            <h4 className="text-sm font-semibold text-foreground">{numberTitle}</h4>
            <p className="text-sm text-muted-foreground">
              We&apos;ll send a code to the new number to make sure it&apos;s yours.
              {user?.phoneNumber &&
                ` Sign-in codes keep going to ${displayPhone(
                  user.phoneNumber
                )} until you confirm it.`}
            </p>
          </div>
          {formError && <Alert tone="danger">{formError}</Alert>}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <FormField label="New number" hint="Indian mobile numbers (+91)." error={phoneError}>
              <Input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="98765 43210"
                leading={<WhatsApp />}
                autoFocus
                value={phone}
                onChange={(event) => {
                  setPhone(event.target.value);
                  setPhoneError(null);
                }}
              />
            </FormField>
            <FormField label="Current password" error={passwordError}>
              <PasswordInput
                autoComplete="current-password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setPasswordError(null);
                }}
              />
            </FormField>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose} disabled={busy !== null}>
              Cancel
            </Button>
            <Button type="submit" loading={busy === "send"}>
              Send code
            </Button>
          </div>
        </form>
      ) : (
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            confirm();
          }}
          className="space-y-5"
        >
          <div className="space-y-1">
            <h4 className="text-sm font-semibold text-foreground">Enter the WhatsApp code</h4>
            <p className="text-sm text-muted-foreground">
              We sent a {CODE_LENGTH}-digit code to{" "}
              <span className="font-medium text-foreground">{displayPhone(target)}</span>.
            </p>
          </div>
          {formError && <Alert tone="danger">{formError}</Alert>}
          <FormField label="Code" error={codeError}>
            <OtpInput
              ref={codeInput}
              value={code}
              onChange={(next) => {
                setCode(next);
                setCodeError(null);
              }}
              onComplete={(value) => confirm(value)}
              length={CODE_LENGTH}
              invalid={Boolean(codeError)}
              autoFocus
            />
          </FormField>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap gap-x-4">
              <Button
                variant="link"
                size="sm"
                onClick={resend}
                disabled={resendIn > 0 || busy !== null}
                loading={busy === "resend"}
              >
                {resendIn > 0 ? `Resend code in ${formatCountdown(resendIn)}` : "Resend code"}
              </Button>
              {change && (
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => {
                    setStep("number");
                    setPhone(target);
                    setFormError(null);
                  }}
                  disabled={busy !== null}
                >
                  Use a different number
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={onClose} disabled={busy === "confirm"}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={busy === "confirm"}
                disabled={code.length < CODE_LENGTH || (busy !== null && busy !== "confirm")}
              >
                {change ? "Confirm new number" : "Verify number"}
              </Button>
            </div>
          </div>
        </form>
      )}
    </section>
  );
};

export default PhoneFlow;
